// Deterministic evidence checks for the memory write policy (design 4.3 and 8.4). A model proposes
// whether a statement is stable or temporary. These checks do not trust it: they read the evidence
// quote the model cited and look for fixed English words. English only and PROVISIONAL: in another
// language the markers rarely match, and the policy then sends more facts to review.
export const MEMORY_REMEMBER_PHRASES_V1 = Object.freeze([
    "remember",
    "note that",
    "keep in mind",
    "do not forget",
    "don't forget",
    "for future",
    "from now on",
    "for next time",
    "save this",
]);
export const MEMORY_HABITUAL_PHRASES_V1 = Object.freeze([
    "always",
    "usually",
    "never",
    "every time",
    "generally",
    "normally",
    "whenever",
    "as a rule",
]);
export const MEMORY_TEMPORAL_PHRASES_V1 = Object.freeze([
    "tonight",
    "today",
    "tomorrow",
    "this evening",
    "this morning",
    "this weekend",
    "this week",
    "this trip",
    "this time",
    "for now",
    "right now",
    "on this visit",
    "just once",
    "just this once",
]);
/**
 * Phrases that contain a habitual word but are an idiom, not a habit (Ruling R9): "never mind". They are removed from the
 * text before the habitual scan.
 */
export const MEMORY_HABITUAL_EXCLUSIONS_V1 = Object.freeze(["never mind"]);
/** NFKC, lowercase, typographic apostrophes folded, whitespace collapsed. The one form both checks compare. */
export function normalizeEvidenceText(text) {
    return text.normalize("NFKC").toLowerCase().replace(/[‘’ʼ]/g, "'").replace(/\s+/g, " ").trim();
}
// A phrase counts only as whole words (the text is normalized, so a plain space separates words, and typographic apostrophes
// are already plain). An apostrophe is part of a word, with two exceptions that end the phrase: a possessive `'s` ("tonight's
// dinner") and a quote mark that is not followed by a letter ("'tonight'"). "tonight'll" and "let's" stay different words.
const patternFor = (phrase) => new RegExp(`(?:^|[^a-z0-9']|(?<![a-z0-9])')${phrase}(?:$|[^a-z0-9']|'s(?![a-z0-9'])|'(?![a-z0-9]))`);
const REMEMBER = MEMORY_REMEMBER_PHRASES_V1.map(patternFor);
const HABITUAL = MEMORY_HABITUAL_PHRASES_V1.map(patternFor);
const TEMPORAL = MEMORY_TEMPORAL_PHRASES_V1.map(patternFor);
const HABITUAL_EXCLUSIONS = MEMORY_HABITUAL_EXCLUSIONS_V1.map((phrase) => new RegExp(`(?<=^|[^a-z0-9'])${phrase}(?=$|[^a-z0-9'])`, "g"));
function scanFolded(folded) {
    const habitualText = HABITUAL_EXCLUSIONS.reduce((text, pattern) => text.replace(pattern, " "), folded);
    return {
        remember: REMEMBER.some((pattern) => pattern.test(folded)),
        habitual: HABITUAL.some((pattern) => pattern.test(habitualText)),
        temporal: TEMPORAL.some((pattern) => pattern.test(folded)),
    };
}
/** Scans a text for the three signal word groups. Pure and deterministic. */
export function scanMemoryEvidenceMarkers(quote) {
    return scanFolded(normalizeEvidenceText(quote));
}
/** Whether the whitespace-normalized quote is a substring of one of the messages (the grounding check). */
export function isEvidenceQuoteGrounded(quote, messageTexts) {
    const needle = normalizeEvidenceText(quote);
    if (needle.length === 0)
        return false;
    return messageTexts.some((text) => normalizeEvidenceText(text).includes(needle));
}
/** A sentence ends at . ! ? ; or a line break (Ruling R8). The delimiter belongs to the sentence it ends. */
const SENTENCE_END = /[.!?;\n]/;
/**
 * The sentences of `message` that the folded `needle` touches, joined by a space: the governing sentence of the quote
 * (Ruling R8). A quote that spans sentences yields their union, and a quote that occurs twice yields both places. The
 * message is folded like `normalizeEvidenceText`, except that line breaks are kept until the sentences are cut.
 */
function governingSentences(message, needle) {
    const kept = message
        .normalize("NFKC")
        .toLowerCase()
        .replace(/[‘’ʼ]/g, "'")
        .replace(/\s+/g, (run) => (/[\r\n]/.test(run) ? "\n" : " "))
        .trim();
    const flat = kept.replace(/\n/g, " ");
    const cuts = [0];
    for (let index = 0; index < kept.length; index += 1) {
        if (SENTENCE_END.test(kept.charAt(index)))
            cuts.push(index + 1);
    }
    if (cuts[cuts.length - 1] !== kept.length)
        cuts.push(kept.length);
    const sentences = [];
    for (let from = flat.indexOf(needle); from !== -1; from = flat.indexOf(needle, from + 1)) {
        const to = from + needle.length;
        for (let cut = 0; cut + 1 < cuts.length; cut += 1) {
            const start = cuts[cut];
            const end = cuts[cut + 1];
            if (end > from && start < to)
                sentences.push(flat.slice(start, end));
        }
    }
    return sentences;
}
/**
 * Grounds a quote against the cited customer messages and scans it. The model chooses the quote, so it must not choose what
 * the code scans (Ruling R8): the habitual and temporal words are read from the whole SENTENCE of each message that holds
 * the quote (the union when the quote spans sentences). The remember word is read from the quote itself, so a neighbouring
 * phrase cannot give a fact a remember request the quote does not hold. An invented quote yields no markers, so it can
 * never raise a promotion.
 */
export function groundMemoryEvidence(quote, messageTexts) {
    const needle = normalizeEvidenceText(quote);
    const sentences = needle.length === 0 ? [] : messageTexts.flatMap((text) => governingSentences(text, needle));
    if (sentences.length === 0) {
        return { grounded: false, markers: { remember: false, habitual: false, temporal: false } };
    }
    const read = scanFolded(` ${sentences.join(" ")} `);
    return {
        grounded: true,
        markers: {
            remember: scanFolded(needle).remember,
            habitual: read.habitual,
            temporal: read.temporal,
        },
    };
}
